import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const PortfolioInsightsAnalysts = ({ holdings, analystData = [] }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const symbols = [...new Set(holdings.map(h => h.symbol))];

    const getAnalystForSymbol = (symbol) => {
        return analystData.find(d => d.symbol === symbol);
    };

    const getConsensusColor = (consensus) => {
        if (!consensus) return theme.palette.text.secondary;
        const lower = consensus.toLowerCase();
        if (lower.includes('strong buy')) return theme.palette.success.dark;
        if (lower.includes('buy')) return theme.palette.success.main;
        if (lower.includes('hold')) return theme.palette.warning.main;
        if (lower.includes('sell')) return theme.palette.error.main;
        return theme.palette.text.secondary;
    };

    // Rating distribution bar component
    const RatingBar = ({ data }) => {
        if (!data) return null;

        const total = (data.strongBuy || 0) + (data.buy || 0) + (data.hold || 0) + (data.sell || 0) + (data.strongSell || 0);
        if (total === 0) return <Typography variant="body2">--</Typography>;

        const segments = [
            { value: data.strongBuy || 0, color: '#1b5e20' },  // dark green
            { value: data.buy || 0, color: '#4caf50' },        // green
            { value: data.hold || 0, color: '#ff9800' },       // orange
            { value: data.sell || 0, color: '#f44336' },       // red
            { value: data.strongSell || 0, color: '#b71c1c' }  // dark red
        ];

        return (
            <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', minWidth: 80 }}>
                {segments.map((seg, idx) => {
                    const pct = (seg.value / total) * 100;
                    if (pct === 0) return null;
                    return (
                        <Box
                            key={idx}
                            sx={{
                                width: `${pct}%`,
                                bgcolor: seg.color
                            }}
                        />
                    );
                })}
            </Box>
        );
    };

    if (analystData.length === 0) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="textSecondary">No analyst data available</Typography>
            </Box>
        );
    }

    return (
        <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: isMobile ? 500 : 'auto' }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Symbol</TableCell>
                        <TableCell>Consensus</TableCell>
                        <TableCell align="center">Rating</TableCell>
                        <TableCell align="right">Strong Buy</TableCell>
                        <TableCell align="right">Buy</TableCell>
                        <TableCell align="right">Hold</TableCell>
                        <TableCell align="right">Sell</TableCell>
                        <TableCell align="right">Strong Sell</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {symbols.map(symbol => {
                        const analyst = getAnalystForSymbol(symbol);

                        return (
                            <TableRow key={symbol}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                        {symbol}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        sx={{ color: getConsensusColor(analyst?.consensus) }}
                                    >
                                        {analyst?.consensus || '--'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <RatingBar data={analyst} />
                                </TableCell>
                                <TableCell align="right">
                                    {analyst?.strongBuy ?? '--'}
                                </TableCell>
                                <TableCell align="right">
                                    {analyst?.buy ?? '--'}
                                </TableCell>
                                <TableCell align="right">
                                    {analyst?.hold ?? '--'}
                                </TableCell>
                                <TableCell align="right">
                                    {analyst?.sell ?? '--'}
                                </TableCell>
                                <TableCell align="right">
                                    {analyst?.strongSell ?? '--'}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default PortfolioInsightsAnalysts;